import { useCallback, useEffect, useState } from "react";

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenTarget = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement || doc.webkitFullscreenElement || null;
}

/**
 * Wrapper around the Fullscreen API. iOS Safari on iPhone exposes no element
 * fullscreen at all, so callers must handle `isSupported === false`.
 */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const element = document.documentElement as FullscreenTarget;
    setIsSupported(
      !!(element.requestFullscreen || element.webkitRequestFullscreen)
    );

    const syncState = () => setIsFullscreen(!!getFullscreenElement());
    syncState();

    document.addEventListener("fullscreenchange", syncState);
    document.addEventListener("webkitfullscreenchange", syncState);
    return () => {
      document.removeEventListener("fullscreenchange", syncState);
      document.removeEventListener("webkitfullscreenchange", syncState);
    };
  }, []);

  // Must be called from a user gesture, otherwise the browser rejects it.
  const enter = useCallback(async (target?: HTMLElement | null) => {
    if (getFullscreenElement()) return true;

    const element = (target || document.documentElement) as FullscreenTarget;
    const request = element.requestFullscreen || element.webkitRequestFullscreen;
    if (!request) return false;

    try {
      await request.call(element, { navigationUI: "hide" });
      return true;
    } catch (error) {
      console.warn("Could not enter fullscreen:", error);
      return false;
    }
  }, []);

  const exit = useCallback(async () => {
    if (!getFullscreenElement()) return true;

    const doc = document as FullscreenDocument;
    const request = doc.exitFullscreen || doc.webkitExitFullscreen;
    if (!request) return false;

    try {
      await request.call(doc);
      return true;
    } catch (error) {
      console.warn("Could not exit fullscreen:", error);
      return false;
    }
  }, []);

  const toggle = useCallback(
    (target?: HTMLElement | null) =>
      getFullscreenElement() ? exit() : enter(target),
    [enter, exit]
  );

  return { isFullscreen, isSupported, enter, exit, toggle };
}
